// @flow

import classnames from 'classnames';
import React, { Fragment, useContext } from 'react';

import type { GQLProps, CMSProps } from 'Client/cms/toolkit/destinations/destinations.resolver';
import { addTranslationKeys } from 'Src/i18n';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';

import { Destinations } from 'Components/widgets/destinations/Destinations';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { LineTitle } from 'Components/widgets/line-title/LineTitle';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { TrackClick } from 'Tracking/track-click/TrackClick';

import {
  getOtherDestinations,
  getPricedDestinations,
  getTopDestinations,
} from './DestinationsLine.utils';

export type LineProps = {
  ...$Exact<CMSProps>,
  ...$Exact<GQLProps>,
};

export const DestinationsLine = ({
  areas,
  config,
  destinations,
  id,
  productType,
  seeAllDestinationsLink: { label: seeAllDestinationsLabel, url: seeAllDestinationsUrl },
  viewMapLink: { label: viewMapLabel, url: viewMapUrl },
  title,
  topDestinations,
}: LineProps) => {
  const { isDesktop } = useContext(ResponsiveContext);
  addTranslationKeys(config);

  const { pricedAreas, pricedCountries } = getPricedDestinations(destinations);
  const topAreas = getTopDestinations(topDestinations, { pricedAreas, pricedCountries });
  const otherAreas = getOtherDestinations(areas, { pricedAreas, pricedCountries });
  const allAreas = [topAreas, ...otherAreas];

  const trackingData = {
    actionGA: 'push_content_destinations',
  };

  const hasAllDestinationsLink = Boolean(seeAllDestinationsLabel) && Boolean(seeAllDestinationsUrl);
  const hasViewMapLink = Boolean(viewMapLabel) && Boolean(viewMapUrl);

  const header = (
    <Fragment>
      <LineTitle className="flex-1 md:flex-none">{title}</LineTitle>
      <div className="m-auto md:mx-0 mt-4 md:mt-1 order-last md:order-none">
        {hasViewMapLink && (
          <TrackClick
            as={DynamicLink}
            className="text-base text-gray-darker hover:text-gray-active focus:text-gray-active underline"
            trackingData={{ ...trackingData, labelGA: 'viewMap' }}
            url={viewMapUrl}
          >
            {viewMapLabel}
          </TrackClick>
        )}
        {hasAllDestinationsLink && (
          <TrackClick
            as={DynamicLink}
            className={classnames(
              'text-base text-gray-darker hover:text-gray-active focus:text-gray-active underline',
              {
                'rtl:ml-4': hasViewMapLink,
              },
            )}
            trackingData={{ ...trackingData, labelGA: 'seeAllDestinations' }}
            url={seeAllDestinationsUrl}
          >
            {seeAllDestinationsLabel}
          </TrackClick>
        )}
      </div>
    </Fragment>
  );

  const desktopHeader = isDesktop ? (
    <div className="flex justify-between w-full">{header}</div>
  ) : null;

  return (
    <SiteContainer id={id} className="Line" dataTestId="Line-Destinations">
      <div className="flex flex-wrap justify-between">
        {isDesktop ? null : header}
        <Destinations
          destinations={allAreas}
          header={desktopHeader}
          productType={productType}
          trackingData={trackingData}
        />
      </div>
    </SiteContainer>
  );
};
